import { useEffect, useRef, useState } from 'react';

import { BannerPlacement } from '../../../../typings/enums';
import { window } from '../../../globals';
import { hardRedirectTo } from '../../../utils';
import BannerManager from '../../BannerManager';
import { buildRegisterHref } from '../buildRegisterHref';
import SignInSignUpToggle from '../components/SignInSignUpToggle';
import useAuthState from '../hooks/useAuthState';
import AuthLayout from '../layout/AuthLayout';
import EnterIdentifierStep from '../steps/EnterIdentifierStep';
import MethodsStep from '../steps/MethodsStep';
import MfaStep from '../steps/MfaStep';
import OrgPickerStep from '../steps/OrgPickerStep';
import type { MfaMethodType } from '../../../../shared/model-typings/mfaMethod';
import type { Candidate, ResolveUserResponse, SignupSuggestion } from '../../../../shared/types/routes/auth/v2';
import type { RouterProps } from '../../../types';

export type LoginPageProps = {
  location: RouterProps['location'];
};

export type LoginStep =
  | { kind: 'enter_identifier' }
  | { kind: 'pick_org'; candidates: Candidate[]; signupSuggestions: SignupSuggestion[] }
  | { kind: 'methods'; org: Candidate }
  | { kind: 'mfa'; mfaMethod: MfaMethodType };

const Login = ({ location }: LoginPageProps) => {
  const [identifier, setIdentifier] = useState(location.query.email ?? '');
  const [step, setStep] = useState<LoginStep>({ kind: 'enter_identifier' });
  const { data: authState } = useAuthState();
  const appliedInitialState = useRef(false);

  // Both the idle and mfa phases carry the configured OAuth buttons, so backing out of a pending challenge to the
  // identifier step still offers social login.
  const oauthProviders =
    authState && (authState.kind === 'idle' || authState.kind === 'mfa') ? authState.oauthProviders : [];

  // Resume an in-progress MFA challenge after a refresh: when the mount-time auth-state fetch reports a pending
  // challenge, jump straight to the MFA step. Gated to fire once so navigating back out of MFA (while the server
  // session is still alive) doesn't bounce the user straight back in.
  useEffect(() => {
    if (appliedInitialState.current || !authState) {
      return;
    }
    appliedInitialState.current = true;
    if (authState.kind === 'mfa') {
      setStep({ kind: 'mfa', mfaMethod: authState.mfaMethod });
    }
  }, [authState]);

  const handleResolved = (response: ResolveUserResponse) => {
    const { candidates, signupSuggestions } = response;
    const [firstCandidate] = candidates;

    if (firstCandidate && candidates.length === 1 && signupSuggestions.length === 0) {
      setStep({ kind: 'methods', org: firstCandidate });
    } else if (candidates.length > 0 || signupSuggestions.length > 0) {
      setStep({ kind: 'pick_org', candidates, signupSuggestions });
    } else {
      hardRedirectTo(buildRegisterHref(identifier, location.query.next));
    }
  };

  const advanceToMethods = (org: Candidate) => setStep({ kind: 'methods', org });
  const resetToIdentifier = () => setStep({ kind: 'enter_identifier' });
  const requireMfa = (mfaMethod: MfaMethodType) => setStep({ kind: 'mfa', mfaMethod });

  const renderStep = () => {
    switch (step.kind) {
      case 'enter_identifier':
        return (
          <EnterIdentifierStep
            identifier={identifier}
            onIdentifierChange={setIdentifier}
            oauthProviders={oauthProviders}
            onResolved={handleResolved}
          />
        );
      case 'pick_org':
        return (
          <OrgPickerStep
            candidates={step.candidates}
            signupSuggestions={step.signupSuggestions}
            onPickCandidate={advanceToMethods}
            onBack={resetToIdentifier}
          />
        );
      case 'methods':
        return (
          <MethodsStep
            org={step.org}
            identifier={identifier}
            next={location.query.next}
            onMfaRequired={requireMfa}
            onBack={resetToIdentifier}
          />
        );
      case 'mfa':
        return <MfaStep mfaMethod={step.mfaMethod} next={location.query.next} onBack={resetToIdentifier} />;
      default: {
        const exhaustiveCheck: never = step;
        return exhaustiveCheck;
      }
    }
  };

  return (
    <AuthLayout>
      {renderStep()}
      <SignInSignUpToggle identifier={identifier} next={location.query.next} />
      {window.on_prem && <BannerManager placement={BannerPlacement.Login} />}
    </AuthLayout>
  );
};

export default Login;
